if (typeof HeroCarousel === 'undefined') {
    class HeroCarousel extends HTMLElement {
        connectedCallback() {
            this.slideContentHeight = 0;
            this.handleResize = this.handleResize.bind(this);

            const swiperContainer = this.querySelector('.swiper');
            if (!swiperContainer || this.swiper) return;

            whenReady('Swiper', () => {
                try {
                    this.swiper = new Swiper(swiperContainer, {
                        loop: true,
                        a11y: true,
                        pauseOnMouseEnter: false,
                        modules: [Swiper.EffectFade, Swiper.Autoplay, Swiper.A11y, Swiper.Pagination],
                        pagination: {
                            el: this.querySelector('.swiper-pagination'),
                            type: 'bullets',
                            clickable: true
                        }
                    });

                    this.updateSlideContentHeight(); // Initial setup
                    window.addEventListener('resize', this.handleResize);

                    this.swiper.on('slideChange', () => {
                        if(this.swiper.slides[this.swiper.activeIndex].querySelector("video") !== null){
                            const video = this.swiper.slides[this.swiper.activeIndex].querySelector("video");
                            if (video) {
                                // Preserve muted state if user has previously unmuted this video
                                // Only set to muted if it hasn't been explicitly unmuted by user
                                const wasUnmuted = video.dataset.userUnmuted === 'true';
                                if (!wasUnmuted) {
                                    video.muted = true; // Muted videos have better autoplay support
                                }
                                
                                video.playsInline = true; // Prevents fullscreen on iOS
                                
                                // Try to play with a small delay to ensure DOM is ready
                                setTimeout(() => {
                                    const playPromise = video.play();
                                    if (playPromise !== undefined) {
                                        playPromise.catch(error => {
                                            // Autoplay was prevented or failed
                                            console.log('Video autoplay prevented:', error);
                                            
                                            // Try one more time with user interaction simulation
                                            // This is a last resort and may not work on all devices
                                            try {
                                                video.currentTime = 0;
                                                video.play();
                                            } catch (retryError) {
                                                console.log('Video autoplay retry failed:', retryError);
                                            }
                                        });
                                    }
                                }, 100);
                            }
                        }
                    });

                    if (this.querySelectorAll(".swiper-next-button").length) {
                        this.querySelectorAll(".swiper-next-button").forEach($button => {
                            $button.addEventListener("click", e => {
                                e.preventDefault();
                                this.swiper.slideNext();
                            });
                        });
                    }
                } catch (error) {
                    console.error('Failed to initialize Swiper:', error);
                }
            });
        }

        disconnectedCallback() {
            if (this.swiper) {
                this.swiper.destroy(true, true);
                this.swiper = null;
            }
            window.removeEventListener('resize', this.handleResize);
        }

        updateSlideContentHeight() {
            
            this.slideContentHeight = 0;
            const slides = this.querySelectorAll(".hero-v2__content > div");

            slides.forEach($slide => {
                $slide.style.height = 'auto'; // Reset before measuring
                if ($slide.clientHeight > this.slideContentHeight) {
                    this.slideContentHeight = $slide.clientHeight;
                }
            });

            slides.forEach($slide => {
                $slide.style.height = `${this.slideContentHeight}px`;
            });

        }

        handleResize() {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
                this.updateSlideContentHeight();
            }, 150);
        }
    }

    if (!window.customElements.get('hero-carousel')) {
        window.customElements.define('hero-carousel', HeroCarousel);
    }
}

if (typeof HeroAudio === 'undefined') {
    class HeroAudio extends HTMLElement {
        constructor() {
            super();

            this.isLoaded = false;
            this.isPlaying = false;
            this.audio = null;
            this.observer = null;

            this.handlePlayClick = this.handlePlayClick.bind(this);
            this.handlePauseClick = this.handlePauseClick.bind(this);
            this.handleAudioPlay = this.handleAudioPlay.bind(this);
            this.handleAudioPause = this.handleAudioPause.bind(this);
            this.handleAudioEnded = this.handleAudioEnded.bind(this);
            this.handleIntersection = this.handleIntersection.bind(this);
            this.handleContainerClick = this.handleContainerClick.bind(this);
            this.handleKeydown = this.handleKeydown.bind(this);
            this.togglePlayback = this.togglePlayback.bind(this);
        }

        connectedCallback() {
            this.mp3 = this.getAttribute('mp3');
            if (!this.mp3) {
                console.warn('HeroAudio: missing `mp3` attribute.');
                return;
            }

            this.classList.add('hero-audio');
            this.setAttribute('role', 'group');

            const buttons = Array.from(this.querySelectorAll('button'));
            this.playButton = buttons[0] || this.createFallbackButton('Play');
            this.pauseButton = buttons[1] || this.createFallbackButton('Pause');

            [this.playButton, this.pauseButton].forEach(button => {
                if (!button.hasAttribute('type')) {
                    button.type = 'button';
                }
                button.classList.add('hero-audio__button');
            });

            this.playButton.addEventListener('click', this.handlePlayClick);
            this.pauseButton.addEventListener('click', this.handlePauseClick);

            this.setPlayingState(false);

            this.addEventListener('click', this.handleContainerClick);
            this.addEventListener('keydown', this.handleKeydown);

            if (!this.hasAttribute('tabindex')) {
                this.setAttribute('tabindex', '0');
            }

            if ('IntersectionObserver' in window) {
                this.observer = new IntersectionObserver(this.handleIntersection, {
                    root: null,
                    threshold: 0.25
                });
                this.observer.observe(this);
            } else {
                this.loadAudio();
            }
        }

        disconnectedCallback() {
            this.detachObserver();
            this.teardownAudio();

            if (this.playButton) {
                this.playButton.removeEventListener('click', this.handlePlayClick);
            }
            if (this.pauseButton) {
                this.pauseButton.removeEventListener('click', this.handlePauseClick);
            }
            this.removeEventListener('click', this.handleContainerClick);
            this.removeEventListener('keydown', this.handleKeydown);
        }

        static get observedAttributes() {
            return ['mp3'];
        }

        attributeChangedCallback(name, _oldValue, newValue) {
            if (name === 'mp3' && newValue !== this.mp3) {
                this.mp3 = newValue;
                if (this.isLoaded) {
                    this.resetAudio();
                }
            }
        }

        createFallbackButton(label) {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = label;
            this.appendChild(button);
            return button;
        }

        handleIntersection(entries) {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadAudio();
                    if (this.observer) {
                        this.observer.disconnect();
                        this.observer = null;
                    }
                }
            });
        }

        loadAudio() {
            if (this.isLoaded || !this.mp3) {
                return;
            }

            this.audio = new Audio();
            this.audio.src = this.mp3;
            this.audio.preload = 'auto';
            this.audio.loop = this.hasAttribute('loop');

            this.audio.addEventListener('play', this.handleAudioPlay);
            this.audio.addEventListener('pause', this.handleAudioPause);
            this.audio.addEventListener('ended', this.handleAudioEnded);

            this.isLoaded = true;
            this.setAttribute('data-audio-loaded', 'true');
        }

        handlePlayClick(event) {
            event.preventDefault();
            event.stopPropagation();
            this.play();
        }

        handlePauseClick(event) {
            event.preventDefault();
            event.stopPropagation();
            this.pause();
        }

        handleContainerClick(event) {
            if (event.target.closest('button')) {
                return;
            }
            this.togglePlayback();
        }

        handleKeydown(event) {
            if (event.target !== this) {
                return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.togglePlayback();
            }
        }

        play() {
            this.loadAudio();
            if (!this.audio) {
                return;
            }

            document.querySelectorAll('hero-audio').forEach(instance => {
                if (instance !== this && typeof instance.pause === 'function') {
                    instance.pause();
                }
            });

            const playPromise = this.audio.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(error => {
                    console.warn('HeroAudio: playback failed.', error);
                    this.setPlayingState(false);
                });
            }
        }

        pause() {
            if (this.audio) {
                this.audio.pause();
            }
        }

        togglePlayback() {
            if (this.isPlaying) {
                this.pause();
            } else {
                this.play();
            }
        }
        handleAudioPlay() {
            this.setPlayingState(true);
        }

        handleAudioPause() {
            this.setPlayingState(false);
        }

        handleAudioEnded() {
            if (this.audio) {
                this.audio.currentTime = 0;
            }
            this.setPlayingState(false);
        }

        setPlayingState(isPlaying) {
            this.isPlaying = isPlaying;
            this.setButtonVisibility(this.playButton, !isPlaying);
            this.setButtonVisibility(this.pauseButton, isPlaying);
            this.toggleAttribute('data-playing', isPlaying);
        }

        setButtonVisibility(button, shouldShow) {
            if (!button) {
                return;
            }
            button.style.display = shouldShow ? 'block' : 'none';
            button.setAttribute('aria-hidden', String(!shouldShow));
        }

        resetAudio() {
            this.teardownAudio();
            this.isLoaded = false;
            this.setPlayingState(false);
            this.removeAttribute('data-audio-loaded');
            this.loadAudio();
        }

        teardownAudio() {
            if (!this.audio) {
                return;
            }
            this.audio.pause();
            this.audio.removeEventListener('play', this.handleAudioPlay);
            this.audio.removeEventListener('pause', this.handleAudioPause);
            this.audio.removeEventListener('ended', this.handleAudioEnded);
            this.audio = null;
        }

        detachObserver() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        }
    }

    if (!window.customElements.get('hero-audio')) {
        window.customElements.define('hero-audio', HeroAudio);
    }
}

if (typeof VideoAudioToggle === 'undefined') {
    class VideoAudioToggle extends HTMLElement {
        constructor() {
            super();
            this.handleClick = this.handleClick.bind(this);
            this.handleVolumeChange = this.handleVolumeChange.bind(this);
        }

        connectedCallback() {
            // Find the buttons within this element
            const buttons = this.querySelectorAll('button');
            this.playButton = buttons[0]; // "Play Audio" button
            this.pauseButton = buttons[1]; // "Pause Audio" button

            // Find the video element - look in the same slide/block context
            // The video should be a sibling or in a nearby parent
            this.video = this.findVideoElement();

            if (!this.video) {
                console.warn('VideoAudioToggle: No video element found');
                return;
            }

            // Store reference to this toggle on the video for easy access
            this.video._audioToggle = this;

            // Check if user has previously unmuted this video
            const wasUnmuted = this.video.dataset.userUnmuted === 'true';
            if (wasUnmuted) {
                this.video.muted = false;
            } else {
                // Ensure video is muted by default
                this.video.muted = true;
            }

            // Set initial button visibility
            this.updateButtonVisibility(this.video.muted);

            // Watch for external changes to muted state (e.g., from resize/slide change)
            this.mutedObserver = new MutationObserver(() => {
                // Update button visibility when muted state changes
                this.updateButtonVisibility(this.video.muted);
                
                // If user previously unmuted this video but it was reset, restore it
                // Use a small delay to avoid infinite loops and let other handlers finish first
                const wasUnmuted = this.video.dataset.userUnmuted === 'true';
                if (wasUnmuted && this.video.muted) {
                    clearTimeout(this.restoreTimeout);
                    this.restoreTimeout = setTimeout(() => {
                        if (this.video && this.video.dataset.userUnmuted === 'true' && this.video.muted) {
                            this.video.muted = false;
                        }
                    }, 50);
                }
            });
            this.mutedObserver.observe(this.video, {
                attributes: true,
                attributeFilter: ['muted']
            });

            // Also listen to volumechange event as a backup
            this.video.addEventListener('volumechange', this.handleVolumeChange);

            // Add click handler to the custom element
            this.addEventListener('click', this.handleClick);
        }

        disconnectedCallback() {
            this.removeEventListener('click', this.handleClick);
            
            if (this.restoreTimeout) {
                clearTimeout(this.restoreTimeout);
            }
            
            if (this.mutedObserver) {
                this.mutedObserver.disconnect();
                this.mutedObserver = null;
            }

            if (this.video) {
                this.video.removeEventListener('volumechange', this.handleVolumeChange);
                delete this.video._audioToggle;
            }
        }

        findVideoElement() {
            // Try to find video in the same slide/block
            // Look for video with class 'hero__asset' in the same swiper slide
            let current = this.parentElement;
            
            // Traverse up to find the swiper slide
            while (current && !current.classList.contains('swiper-slide')) {
                current = current.parentElement;
            }

            if (current) {
                // Look for video in the slide
                const video = current.querySelector('video.hero__asset');
                if (video) return video;
            }

            // Fallback: look for any video in nearby context
            const heroCarousel = this.closest('hero-carousel');
            if (heroCarousel) {
                const activeSlide = heroCarousel.querySelector('.swiper-slide-active');
                if (activeSlide) {
                    const video = activeSlide.querySelector('video');
                    if (video) return video;
                }
            }

            // Last resort: find any video in the document (not ideal but works)
            return document.querySelector('video.hero__asset');
        }

        handleClick(event) {
            event.preventDefault();
            event.stopPropagation();

            if (!this.video) return;

            // Toggle muted state
            this.video.muted = !this.video.muted;

            // Store user's preference - if they unmuted it, remember that
            if (!this.video.muted) {
                this.video.dataset.userUnmuted = 'true';
            } else {
                // If they mute it again, we can remove the preference (optional)
                // Or keep it so they can toggle back easily
                // this.video.dataset.userUnmuted = 'false';
            }
            
            // Update button visibility based on muted state
            this.updateButtonVisibility(this.video.muted);
        }

        handleVolumeChange() {
            // Update button visibility when muted state changes externally
            this.updateButtonVisibility(this.video.muted);
        }

        updateButtonVisibility(isMuted) {
            // When muted: show play button (display: flex), hide pause button (display: none)
            // When unmuted: hide play button (display: none), show pause button (display: flex)
            
            if (this.playButton) {
                this.playButton.style.display = isMuted ? 'flex' : 'none';
                this.playButton.hidden = !isMuted;
            }

            if (this.pauseButton) {
                this.pauseButton.style.display = isMuted ? 'none' : 'flex';
                this.pauseButton.hidden = isMuted;
            }
        }
    }
    if (!window.customElements.get('video-audio-toggle')) {
        window.customElements.define('video-audio-toggle', VideoAudioToggle);
    }
}